import React, { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { ClientConfiguration } from "../../models/ClientConfiguration/ClientConfiguration";
import { DropboxRootFolder } from "../../models/ClientConfiguration/DropboxRootFolder";
import OrganizationsRepository from "../../repositories/OrganizationsRepository";
import { DropboxRefreshToken } from "../../models/OrganizationConfiguration/DropboxRefreshToken";
import OutlinedDiv from "../common/OutlinedDiv";
import DropboxRootFoldersConfigurationControl from "./DropboxRootFoldersConfigurationControl";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { EyeOff, Eye } from "lucide-react";
import { ImplementationType } from "../../models/OrganizationConfiguration/ImplementationType";
import FieldError from "../common/FieldError";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { MASKED_CREDENTIAL } from "../../constants/Credentials";

interface ApiSectionProps {
  organizationId: string;
  implementationType: ImplementationType;
  disabled?: boolean;
}

const ApiSection: React.FC<ApiSectionProps> = ({
  organizationId,
  implementationType,
  disabled = false,
}) => {
  const form = useFormContext<ClientConfiguration>();
  const organizationsRepository = new OrganizationsRepository();
  const [dropboxRefreshTokens, setDropboxRefreshTokens] = useState<DropboxRefreshToken[]>([]);
  const [showFilevinePersonalAccessToken, setShowFilevinePersonalAccessToken] = useState(false);
  const [showFilevineClientId, setShowFilevineClientId] = useState(false);
  const [showFilevineClientSecret, setShowFilevineClientSecret] = useState(false);
  const [showDropboxAppSecret, setShowDropboxAppSecret] = useState(false);

  const sourceApiMaxRequestsPerSecond = form.watch("sourceApiMaxRequestsPerSecond");
  const sourceApiMaxRequestsPerHour = form.watch("sourceApiMaxRequestsPerHour");
  const sourceApiMaxRequestsPerDay = form.watch("sourceApiMaxRequestsPerDay");
  const filevinePersonalAccessToken = form.watch("filevinePersonalAccessToken");
  const filevineClientId = form.watch("filevineClientId");
  const filevineClientSecret = form.watch("filevineClientSecret");
  const filevineUseBatchEndpoint = form.watch("filevineUseBatchEndpoint");
  const dropboxApiId = form.watch("dropboxApiId");
  const dropboxAppSecret = form.watch("dropboxAppSecret");
  const dropboxRootFolders = form.watch("dropboxRootFolders");

  const dropboxRefreshTokensQuery = useQuery({ queryKey: [QUERY_KEYS.DROPBOX_REFRESH_TOKENS, organizationId], queryFn:
    async () => {
      return await organizationsRepository.getOrganizationDropboxRefreshTokens(
        organizationId
      );
    }, gcTime: 2 * 60 * 1000,
      refetchInterval: 2 * 60 * 1000,
      refetchOnMount: false, });

  useEffect(() => {
    if (dropboxRefreshTokensQuery.isSuccess && dropboxRefreshTokensQuery.data)
      setDropboxRefreshTokens(dropboxRefreshTokensQuery.data);
  }, [
    dropboxRefreshTokensQuery.data,
    dropboxRefreshTokensQuery.isSuccess,
    dropboxRefreshTokensQuery.isLoading,
    dropboxRefreshTokensQuery.isError,
  ]);

  const handleMaxRequestsPerSecondChange = (maxRequestsPerSecond: string) => {
    if (maxRequestsPerSecond.trim() === "") {
      form.setValue("sourceApiMaxRequestsPerSecond", null);
      return;
    }
    const parsed = parseInt(maxRequestsPerSecond);
    if (!isNaN(parsed) && parsed > 0) {
      form.setValue("sourceApiMaxRequestsPerSecond", parsed);
    }
  };

  const handleMaxRequestsPerHourChange = (maxRequestsPerHour: string) => {
    if (maxRequestsPerHour.trim() === "") {
      form.setValue("sourceApiMaxRequestsPerHour", null);
      return;
    }
    const parsed = parseInt(maxRequestsPerHour);
    if (!isNaN(parsed) && parsed > 0) {
      form.setValue("sourceApiMaxRequestsPerHour", parsed);
    }
  };

  const handleMaxRequestsPerDayChange = (maxRequestsPerDay: string) => {
    if (maxRequestsPerDay.trim() === "") {
      form.setValue("sourceApiMaxRequestsPerDay", null);
      return;
    }
    const parsed = parseInt(maxRequestsPerDay);
    if (!isNaN(parsed) && parsed > 0) {
      form.setValue("sourceApiMaxRequestsPerDay", parsed);
    }
  };

  const handleDropboxRootFoldersChange = (folders: DropboxRootFolder[]) => {
    form.setValue("dropboxRootFolders", folders);
  };

  const dropboxRefreshTokenId = form.watch("dropboxRefreshTokenId");
  const selectedToken = dropboxRefreshTokens.find(
    (token) => token.id === dropboxRefreshTokenId
  );

  return (
    <>
      <div className="grid grid-cols-12 gap-2 mb-0">
        <div className="col-span-4 min-w-[25%]">
          <div>
            <Label>Request limit per second</Label>
            <Input
              disabled={disabled}
              type="number"
              onChange={(event) =>
                handleMaxRequestsPerSecondChange(event.target.value)
              }
              value={
                sourceApiMaxRequestsPerSecond === null
                  ? ""
                  : sourceApiMaxRequestsPerSecond
              }
            />
            <FieldError name="sourceApiMaxRequestsPerSecond" />
          </div>
        </div>

        <div className="col-span-4 min-w-[25%]">
          <div>
            <Label>Request limit per hour</Label>
            <Input
              disabled={disabled}
              type="number"
              onChange={(event) =>
                handleMaxRequestsPerHourChange(event.target.value)
              }
              value={
                sourceApiMaxRequestsPerHour === null
                  ? ""
                  : sourceApiMaxRequestsPerHour
              }
            />
            <FieldError name="sourceApiMaxRequestsPerHour" />
          </div>
        </div>

        <div className="col-span-4 min-w-[25%]">
          <div>
            <Label>Request limit per day</Label>
            <Input
              disabled={disabled}
              type="number"
              onChange={(event) =>
                handleMaxRequestsPerDayChange(event.target.value)
              }
              value={
                sourceApiMaxRequestsPerDay === null
                  ? ""
                  : sourceApiMaxRequestsPerDay
              }
            />
            <FieldError name="sourceApiMaxRequestsPerDay" />
          </div>
        </div>
      </div>

      {implementationType === ImplementationType.Filevine && (
        <>
          <div className="mb-1">
            <Label>Personal Access Token</Label>
            <div className="relative flex items-center">
              <Input
                disabled={disabled}
                type={showFilevinePersonalAccessToken ? "text" : "password"}
                value={filevinePersonalAccessToken}
                onChange={(event) =>
                  form.setValue("filevinePersonalAccessToken", event.target.value)
                }
                className="pr-10"
              />
              <div className="absolute right-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setShowFilevinePersonalAccessToken((prev) => !prev)}
                >
                  {showFilevinePersonalAccessToken ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </Button>
              </div>
            </div>
            <FieldError name="filevinePersonalAccessToken" />
          </div>

          <div className="mb-1">
            <Label>Client ID</Label>
            <div className="relative flex items-center">
              <Input
                disabled={disabled}
                type={showFilevineClientId ? "text" : "password"}
                value={filevineClientId}
                onChange={(event) =>
                  form.setValue("filevineClientId", event.target.value)
                }
                className="pr-10"
              />
              <div className="absolute right-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setShowFilevineClientId((prev) => !prev)}
                >
                  {showFilevineClientId ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </Button>
              </div>
            </div>
            <FieldError name="filevineClientId" />
          </div>

          <div className="mb-3">
            <Label>Client Secret</Label>
            <div className="relative flex items-center">
              <Input
                disabled={disabled}
                type={showFilevineClientSecret ? "text" : "password"}
                value={filevineClientSecret}
                onChange={(event) =>
                  form.setValue("filevineClientSecret", event.target.value)
                }
                className="pr-10"
              />
              <div className="absolute right-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setShowFilevineClientSecret((prev) => !prev)}
                >
                  {showFilevineClientSecret ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </Button>
              </div>
            </div>
            <FieldError name="filevineClientSecret" />
          </div>

          <OutlinedDiv label="">
            <div
              className={cn(
                "flex w-full justify-between items-center",
                disabled && "opacity-50 pointer-events-none"
              )}
            >
              <Label>Use Batch Endpoint</Label>
              <Switch
                checked={filevineUseBatchEndpoint}
                onCheckedChange={(checked) =>
                  form.setValue("filevineUseBatchEndpoint", checked)
                }
              />
            </div>
          </OutlinedDiv>
        </>
      )}

      {implementationType === ImplementationType.Dropbox && (
        <>
          <div className="mb-1">
            <Label>API ID</Label>
            <Input
              value={dropboxApiId}
              onChange={(event) =>
                form.setValue("dropboxApiId", event.target.value)
              }
              disabled={disabled}
            />
            <FieldError name="dropboxApiId" />
          </div>

          <div className="mb-1">
            <Label>App Secret</Label>
            <div className="relative flex items-center">
              <Input
                type={showDropboxAppSecret ? "text" : "password"}
                value={dropboxAppSecret}
                onChange={(event) =>
                  form.setValue("dropboxAppSecret", event.target.value)
                }
                disabled={disabled}
                className="pr-10"
              />
              <div className="absolute right-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setShowDropboxAppSecret((prev) => !prev)}
                >
                  {showDropboxAppSecret ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </Button>
              </div>
            </div>
            <FieldError name="dropboxAppSecret" />
          </div>

          <div>
            <Label>Refresh Token</Label>
            <div className={cn(dropboxRefreshTokensQuery.isLoading && "opacity-50 pointer-events-none")}>
              <Select
                value={selectedToken ? selectedToken.id : undefined}
                onValueChange={(tokenId) => {
                  const token = dropboxRefreshTokens.find(t => t.id === tokenId);
                  // The portal never holds token values: the server copies the selected token's value on save.
                  form.setValue("dropboxRefreshTokenId", token?.id ?? null);
                  form.setValue("dropboxRefreshToken", token ? MASKED_CREDENTIAL : "");
                  form.setValue("dropboxAccountType", token?.accountType);
                }}
                disabled={disabled}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a token" />
                </SelectTrigger>
                <SelectContent>
                  {dropboxRefreshTokens.length > 0 ? (
                    dropboxRefreshTokens.map((token: DropboxRefreshToken) => (
                      <SelectItem key={token.id} value={token.id}>
                        {token.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem disabled value="__none__">
                      No tokens available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <FieldError name="dropboxRefreshToken" />
          </div>

          <DropboxRootFoldersConfigurationControl
            dropboxRootFolders={dropboxRootFolders || []}
            onChange={handleDropboxRootFoldersChange}
            disabled={disabled}
          />
        </>
      )}
    </>
  );
};

export default ApiSection;
